import {
  Component,
  Input,
  forwardRef,
  ElementRef,
  Renderer2,
  RendererStyleFlags2,
  AfterViewInit
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IImageZone, IImageZoneColors } from '../../core/interfaces/image-zone.interface';

const DEFAULT_COLORS: Required<IImageZoneColors> = {
  hoverStroke: '#ff8080',
  hoverFill: 'rgba(255, 128, 128, 0.2)',
  activeStroke: '#ff0000',
  activeFill: 'rgba(255, 128, 128, 0.45)'
};

@Component({
  selector: 'app-image-zone-selector',
  templateUrl: './image-zone-selector.component.html',
  styleUrls: ['./image-zone-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageZoneSelectorComponent),
      multi: true
    }
  ]
})
export class ImageZoneSelectorComponent implements ControlValueAccessor, AfterViewInit {
  @Input() image: string;
  @Input() zones: IImageZone[] = [];
  @Input() viewBox = '0 0 1402 1122';
  @Input() showStroke = true;
  @Input() colors: IImageZoneColors = {};

  selected: string[] = [];
  disabled = false;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngAfterViewInit(): void {
    this.applyColors();
  }

  private applyColors(): void {
    const c = { ...DEFAULT_COLORS, ...this.colors };
    const vars: { [key: string]: string } = {
      '--zone-hover-stroke': c.hoverStroke,
      '--zone-hover-fill': c.hoverFill,
      '--zone-active-stroke': c.activeStroke,
      '--zone-active-fill': c.activeFill
    };
    Object.keys(vars).forEach(name => {
      this.renderer.setStyle(this.el.nativeElement, name, vars[name], RendererStyleFlags2.DashCase);
    });
  }

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string[]): void {
    this.selected = Array.isArray(value) ? value : [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggle(zone: IImageZone): void {
    if (this.disabled) {
      return;
    }
    if (this.isActive(zone)) {
      this.selected = this.selected.filter(n => zone.zoneNames.indexOf(n) === -1);
    } else {
      this.selected = [...this.selected, ...zone.zoneNames];
    }
    this.onChange(this.selected);
    this.onTouched();
  }

  isActive(zone: IImageZone): boolean {
    return zone.zoneNames.some(n => this.selected.indexOf(n) > -1);
  }
}
